import React from "react";
import { useSelector } from "react-redux";
import { NavigationType, useNavigationType } from "react-router-dom";
import { AppState } from "store";
//#region types
import { TranslationKeys } from "translations";
import { NavMenuItem } from "models/propTypes/pageLayoutPropTypes";
import { AuthEnums } from "models/enums";
import { IconDefinitionConstants, RouteConstants, SettingsConstants } from "utils/constants";
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
//#endregion
//#region components
import { BreadCrumb, PageLayouts, SettingsFormViewPanel, useCheckRelations } from "components/common";
import BankBalanceSettingsFormContainer from "./forms/bankBalanceSettingsFormContainer";
import CompanySettingsFormContainer from "./forms/companySettingsFormContainer";
import DimensionSettingsFormContainer from "./forms/dimensionSettingsFormContainer";
import EmployeesSettingsFormContainer from "./forms/employeesSettingsFormContainer";
import IntegrationSettingsFormContainer from "./forms/integrationSettingsFormContainer";
import InvoiceSettingsFormContainer from "./forms/invoiceSettingsFormContainer";
import ProfileSettingsFormContainer from "./forms/profileSettingsFormContainer";
import { useEmployeeHelpers } from "components/employee";
import AboutPage from "./systemInfo/about";
//#endregion
import { LocalizationHelperInstance, UrlHelper } from "utils/helpers";
import { CompanyService } from "services";

const Settings: React.FC = () => {
  const [selectedTitle, setSelectedTitle] = React.useState<string>();
  // Read once on arrival: selecting the section replaces the URL before the user list mounts, so the
  // list itself only ever sees a replace. A history step back onto the user list (browser back, or
  // closing the user editor) returns it to where it was; following a link starts it from page one.
  // Settings stays mounted across sections, so leaving the user list ends the restore.
  const navigationType = useNavigationType();
  const [restoreUserList, setRestoreUserList] = React.useState(
    () =>
      navigationType === NavigationType.Pop &&
      new URLSearchParams(window.location.search).get(RouteConstants.QueryParamStrings.Section) ===
        SettingsConstants.NavigationKey.Employees
  );
  const isAuditor = CompanyService.hasRelation(AuthEnums.PersonCompanyRelationType.Auditor);
  const isInvoiceAdmin = CompanyService.hasRelation(AuthEnums.PersonCompanyRelationType.InvoicesAdmin);
  const isSalaryAdmin = CompanyService.hasRelation(AuthEnums.PersonCompanyRelationType.SalaryAdmin, true);

  const { getIsAdmin } = useCheckRelations();
  const isAdmin = getIsAdmin();

  const [selectedCompany] = useSelector((state: AppState) => [state.auth.selectedCompany]);
  const { canCreate: canCreateEmployees, canSalaryAdminManageSalary } = useEmployeeHelpers();

  const isSalaryAvailable =
    canSalaryAdminManageSalary || selectedCompany.loggedInUserIsBusinessUnitManagerWithSalaryRights;

  const getMultipleForms = () => {
    const forms = [
      {
        navKey: SettingsConstants.NavigationKey.MyProfile,
        title: TranslationKeys.Settings.MyProfile,
        menuSectionHeader: TranslationKeys.Settings.MySettings,
        content: (
          <SettingsFormViewPanel allowForReadOnlyUsers>
            <ProfileSettingsFormContainer></ProfileSettingsFormContainer>
          </SettingsFormViewPanel>
        ),
        icon: IconDefinitionConstants.WUser,
      },
    ] as NavMenuItem[];

    const companySettings = {
      navKey: SettingsConstants.NavigationKey.Company,
      title: selectedCompany.name ?? TranslationKeys.Settings.SuperkollForm,
      menuSectionHeader: TranslationKeys.Settings.CompanySetting,
      content: (
        <SettingsFormViewPanel>
          <CompanySettingsFormContainer></CompanySettingsFormContainer>
        </SettingsFormViewPanel>
      ),
      icon: IconDefinitionConstants.WHouse,
    };

    const invoiceSettings = {
      navKey: SettingsConstants.NavigationKey.Invoice,
      title: TranslationKeys.Settings.CustomerInvoices,
      content: (
        <SettingsFormViewPanel>
          <InvoiceSettingsFormContainer></InvoiceSettingsFormContainer>
        </SettingsFormViewPanel>
      ),
      icon: IconDefinitionConstants.WSend,
    };

    const employeesSettings = {
      navKey: SettingsConstants.NavigationKey.Employees,
      title: TranslationKeys.Common.Users,
      menuSectionHeader: !isAdmin && isSalaryAvailable ? TranslationKeys.Settings.CompanySetting : undefined,
      content: (
        <SettingsFormViewPanel>
          <EmployeesSettingsFormContainer
            isAdmin={isAdmin}
            isAuditor={isAuditor}
            isSalaryAdmin={isSalaryAdmin}
            superkollSalaryType={selectedCompany.superkollSalaryType}
            isBusinessUnitManagerWithSalaryRights={selectedCompany.loggedInUserIsBusinessUnitManagerWithSalaryRights}
            restoreListPosition={restoreUserList}
          ></EmployeesSettingsFormContainer>
        </SettingsFormViewPanel>
      ),
      icon: IconDefinitionConstants.WUsers,
    };

    const dimensionSettings = {
      navKey: SettingsConstants.NavigationKey.Dimensions,
      title: TranslationKeys.Common.Tags,
      content: (
        <SettingsFormViewPanel>
          <DimensionSettingsFormContainer></DimensionSettingsFormContainer>
        </SettingsFormViewPanel>
      ),
      icon: IconDefinitionConstants.WHashtag,
    };

    const integrationSettings = {
      navKey: SettingsConstants.NavigationKey.Integrations,
      title: TranslationKeys.Common.Integrations,
      content: (
        <SettingsFormViewPanel hideSave>
          <IntegrationSettingsFormContainer></IntegrationSettingsFormContainer>
        </SettingsFormViewPanel>
      ),
      icon: IconDefinitionConstants.WLink,
    };

    const bankBalanceSettings = {
      navKey: SettingsConstants.NavigationKey.BankBalances,
      title: TranslationKeys.Settings.Bank,
      content: (
        <SettingsFormViewPanel>
          <BankBalanceSettingsFormContainer></BankBalanceSettingsFormContainer>
        </SettingsFormViewPanel>
      ),
      icon: IconDefinitionConstants.WBank,
    };

    const aboutSettings = {
      navKey: SettingsConstants.NavigationKey.About,
      menuSectionHeader: TranslationKeys.About.SystemInfo,
      title: TranslationKeys.About.SystemInfo,
      content: (
        <SettingsFormViewPanel hideSave>
          <AboutPage />
        </SettingsFormViewPanel>
      ),
      icon: IconDefinitionConstants.WInfo,
    };

    isAdmin && !isAuditor && forms.push(companySettings);
    isInvoiceAdmin &&
      !isAuditor &&
      selectedCompany.invoiceActivationState === WintSuperkollAPIModel.InvoiceActivationState.Activated &&
      forms.push(invoiceSettings);
    canCreateEmployees && forms.push(employeesSettings);
    isAdmin && !isAuditor && forms.push(dimensionSettings);

    // The monthly report panel (./forms/monthlyReportSettingsFormContainer) is withheld until the API
    // proxy routes its report-recipient endpoint; before that it can only render an error.

    isAdmin && !isAuditor && selectedCompany.bankBalanceWidgetActivated && forms.push(bankBalanceSettings);

    isAdmin && !isAuditor && forms.push(integrationSettings);

    forms.push(aboutSettings);
    return forms;
  };

  const onMenuItemSelect = (title?: string) => {
    if (selectedTitle === TranslationKeys.Common.Users && title !== selectedTitle) setRestoreUserList(false);
    setSelectedTitle(title);
  };

  return (
    <PageLayouts.EntityFullScreen
      entityKey={`settings`}
      id={0}
      navigation={
        <BreadCrumb
          prevItems={[
            {
              text: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Settings),
              href: UrlHelper.formatPath("/Settings"),
            },
          ]}
          current={selectedTitle ? LocalizationHelperInstance.getFormattedMessage(selectedTitle) : ""}
          showHome
        />
      }
    >
      <PageLayouts.SettingsLayout
        title={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Settings)}
        onMenuItemSelectHandler={onMenuItemSelect}
        navItems={getMultipleForms()}
      ></PageLayouts.SettingsLayout>
    </PageLayouts.EntityFullScreen>
  );
};

export default Settings;
